import { ActivityIcon, ChevronRightIcon, CpuIcon } from "lucide-react";
import { Disclosure, DisclosurePanel } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { UserAvatar } from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateHeading,
  EmptyStateIcon,
} from "@/components/ui/empty-state";
import { Loader } from "@/components/ui/loader";
import { Text } from "@/components/ui/text";
import { format } from "@/lib/utils";
import { useAccession } from "../AccessionProvider";

const actionMap: Record<string, string> = {
  "accession.create": "created the accession",
  "accession.update": "updated the accession",
  "accession.cancel": "cancelled the accession",
  "accession.clone": "cloned the accession",
  "accession.restore": "restored the accession",
  "accession.unreceive": "unreceived the accession",
  "accession.delete": "deleted the accession",
  "accession.receive": "received the accession",
  "accession.updateReceive": "updated receive details",
  "accession.refreshStatus": "refreshed the status of the accession",
  "accession.setStatus": "set the status of the accession",
  "accession.setPriority": "set the priority of the accession",
  "accession.updateBilling": "updated billing",
  "accession.updateClient": "updated the client",
  "accession.updateDonor": "updated the donor",
  "accession.moveToSubject": "moved the accession to another donor",
  "accession.updateTests": "updated the tests",
  "accession.editShipping": "updated accession information",
  "accession.updateShipping": "updated accession information",
  "accession.updateTimeZone": "updated the time zone",
  "accession.comment.create": "commented on the accession",
  "accession.comment.update": "updated a comment",
  "accession.note.create": "added a note to the accession",
  "accession.note.update": "updated a note",
  "accession.sample.create": "created a sample",
  "accession.sample.update": "updated a sample",
  "accession.sample.delete": "deleted a sample",
  "accession.addTests": "added tests to the accession",
  "result.enter": "entered a result",
  "result.validate": "validated a result",
  "result.cancel": "cancelled a result",
  "result.restore": "restored a result",
  "result.calculate": "calculated a result",
  "result.clear": "cleared a result",
  "result.update": "updated a result",
  "testOrder.cancel": "cancelled a test order",
  "testOrder.restore": "restored a test order",
  "testOrder.delete": "deleted a test order",
  "testOrder.cancelPanel": "cancelled a panel",
  "testOrder.restorePanel": "restored a panel",
  "testOrder.deletePanel": "deleted a panel",
  "testOrder.assignSamples": "assigned samples to a test order",
  "upload.create": "uploaded a file",
  "upload.delete": "deleted an upload",
  "upload.update": "updated an upload",
  "testRun.create": "created a test run",
  "report.generate": "generated a report",
  "report.delete": "deleted a report",
  "hold.cancel": "cancelled a hold",
  "hold.create": "created a hold",
  "hold.resolve": "resolved a hold",
  "subject.create": "created the subject",
  "subject.update": "updated the subject",
  "subject.delete": "deleted the subject",
  "subject.moveAccessions": "moved accessions",
  "integration.send": "triggered an integration to send",
  "integration.resend": "triggered an integration to resend",
  "integration.retry": "retried an integration send",
  "integration.enable": "enabled an integration",
  "integration.disable": "disabled an integration",
  "integration.setEnabled": "toggled an integration",
  "integration.configUpdate": "updated an integration's configuration",
  "integration.orgConfigUpdate": "updated an integration's org configuration",
};

function ActivityDetails({
  txnUuids,
  accessionId,
  action,
}: {
  txnUuids: string[];
  accessionId: number;
  action: string;
}) {
  const { data, isPending } = trpc.accession.list.activityDetails.useQuery(
    { txnUuids, accessionId, action },
    { staleTime: Number.POSITIVE_INFINITY },
  );

  if (isPending) {
    return (
      <div className="py-2 pl-10">
        <Loader />
      </div>
    );
  }

  const groups = data ?? [];

  if (groups.length === 0) {
    return (
      <div className="py-2 pl-10">
        <Text size="xs" muted>
          No details available
        </Text>
      </div>
    );
  }

  const showGroupHeaders = true;

  return (
    <div className="py-2 pl-10 flex flex-col gap-2">
      {groups.map((group, gi) => (
        <div key={`${group.resource}-${gi}`} className="flex flex-col gap-1">
          {showGroupHeaders && (
            <Text size="xs" className="font-semibold">
              {group.resourceLabel} ({group.action})
            </Text>
          )}
          {group.fields.map((field, fi) => (
            <div
              key={`${field.field}-${fi}`}
              className="flex items-center gap-2"
            >
              <Text size="xs" className="font-medium min-w-32">
                {field.fieldLabel}:
              </Text>
              {field.oldValue != null && field.oldValue !== "" && (
                <Text size="xs" className="text-destructive line-through">
                  {field.oldValue}
                </Text>
              )}
              {field.oldValue != null &&
                field.oldValue !== "" &&
                field.newValue != null &&
                field.newValue !== "" && (
                  <Text size="xs" muted>
                    &rarr;
                  </Text>
                )}
              {field.newValue != null && field.newValue !== "" && (
                <Text size="xs" className="text-green-700">
                  {field.newValue}
                </Text>
              )}
              {(field.oldValue == null || field.oldValue === "") &&
                (field.newValue == null || field.newValue === "") && (
                  <Text size="xs" muted>
                    (cleared)
                  </Text>
                )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function ActivityLog() {
  const { accession } = useAccession();
  const { data, isPending } = trpc.accession.list.activity.useQuery({
    id: accession.id,
  });
  const actions = data || [];

  if (isPending) {
    return (
      <Centered className="p-6">
        <Loader />
      </Centered>
    );
  }

  if (actions.length === 0) {
    return (
      <EmptyState>
        <EmptyStateIcon>
          <ActivityIcon />
        </EmptyStateIcon>
        <EmptyStateHeading>No activity</EmptyStateHeading>
        <EmptyStateDescription>
          No activity has been logged for this accession, or it may not be
          available.
        </EmptyStateDescription>
      </EmptyState>
    );
  }

  return (
    <div className="relative" role="list" aria-label="Activity log">
      {actions.map((action) => {
        const hasDetails = action.txnUuids.length > 0;

        if (!hasDetails) {
          return (
            <div
              key={action.id}
              role="listitem"
              className="flex items-center gap-3 justify-between px-3 py-2 [&_.timeline-line]:first:hidden"
            >
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <div className="relative">
                    <div className="timeline-line absolute w-[1px] inset-y-0 translate-y-[-100%] bg-muted-foreground/50 left-[50%]" />
                    <UserAvatar
                      user={action.user}
                      size="small"
                      className="z-50 relative overflow-visible"
                      fallback={
                        // biome-ignore lint/suspicious/noExplicitAny: it's ok
                        (action.details as any)?.instrument ? (
                          <CpuIcon className="size-3" />
                        ) : undefined
                      }
                    />
                  </div>
                  <Text size="xs" className="font-medium capitalize">
                    {/* biome-ignore lint/suspicious/noExplicitAny: it's ok */}
                    {(action.details as any)?.instrument
                      ? "Instrument"
                      : (action.user?.username ?? "System")}
                  </Text>
                </div>
                <Text className="text-muted-foreground">
                  {action.action
                    ? (actionMap[action.action] ?? action.action)
                    : "performed action"}
                  {action.quantity > 1 && ` (${action.quantity})`}
                </Text>
              </div>
              <Text muted mono size="xs">
                {format(action.txnTimestamp)}
              </Text>
            </div>
          );
        }

        return (
          <Disclosure key={action.id} className="group/activity">
            <div
              role="listitem"
              className="flex items-center gap-3 justify-between px-3 py-2 [&_.timeline-line]:first:hidden"
            >
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <div className="relative">
                    <div className="timeline-line absolute w-[1px] inset-y-0 translate-y-[-100%] bg-muted-foreground/50 left-[50%]" />
                    <UserAvatar
                      user={action.user}
                      size="small"
                      className="z-50 relative overflow-visible"
                      fallback={
                        // biome-ignore lint/suspicious/noExplicitAny: it's ok
                        (action.details as any)?.instrument ? (
                          <CpuIcon className="size-3" />
                        ) : undefined
                      }
                    />
                  </div>
                  <Text size="xs" className="font-medium capitalize">
                    {/* biome-ignore lint/suspicious/noExplicitAny: it's ok */}
                    {(action.details as any)?.instrument
                      ? "Instrument"
                      : (action.user?.username ?? "System")}
                  </Text>
                </div>
                <Button
                  slot="trigger"
                  variant="ghost"
                  className="h-auto py-0 px-0.5 text-muted-foreground flex items-center gap-2"
                >
                  <Text className="text-muted-foreground">
                    {action.action
                      ? (actionMap[action.action] ?? action.action)
                      : "performed action"}
                    {action.quantity > 1 && ` (${action.quantity})`}
                  </Text>
                  <ChevronRightIcon className="size-3 ml-4 transition-all group-data-[expanded=true]/activity:rotate-90" />
                </Button>
              </div>
              <Text muted mono size="xs">
                {format(action.txnTimestamp)}
              </Text>
            </div>
            <DisclosurePanel>
              <ActivityDetails
                txnUuids={action.txnUuids}
                accessionId={accession.id}
                action={action.action ?? ""}
              />
            </DisclosurePanel>
          </Disclosure>
        );
      })}
    </div>
  );
}
